import { Check } from 'iconoir-react'
import { cn } from 'app/lib/utils'
import { tagColorNames, tagSwatchClasses } from 'app/lib/chipColors'
import { presetColors } from 'app/utils/presetColors'
import { TagChip } from './TagChip'

type TagColorPickerProps = {
  /** Selected stored color key (`bg-tags-*`). */
  value: string
  onChange: (colorKey: string) => void
  /** Tag name typed so far; rendered live in the preview chip. */
  previewName?: string
  className?: string
}

/** Preset tag color picker: a live preview of the resulting chip above a
 *  two-column list of named color options with an explicit check on the
 *  selection (the Notion / Google Calendar convention, per Jakob's law).
 *  Shared by the Settings > Tags modal and the inline Create New Tag dialog. */
export function TagColorPicker({ value, onChange, previewName, className }: TagColorPickerProps) {
  return (
    <div className={cn(`flex flex-col gap-2`, className)}>
      <div className="flex min-h-12 items-center justify-center rounded-lg border border-dashed border-default bg-surface-subtle/60 px-3 py-2.5">
        <TagChip name={previewName?.trim() || `Preview`} colorKey={value} />
      </div>
      <div className="grid grid-cols-2 gap-x-1 gap-y-0.5">
        {presetColors.map((colorKey) => {
          const selected = colorKey === value
          return (
            <button
              key={colorKey}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(colorKey)}
              className={cn(
                `flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-body-xs text-strong transition-colors`,
                selected ? `bg-surface-muted hover:bg-surface-muted` : `hover:bg-surface-subtle`,
              )}
            >
              <span
                className={cn(
                  `h-3.5 w-3.5 shrink-0 rounded-full ring-1 ring-inset ring-strong`,
                  tagSwatchClasses(colorKey),
                )}
              />
              <span className="min-w-0 flex-1 truncate">{tagColorNames[colorKey]}</span>
              {selected && <Check className="h-3.5 w-3.5 shrink-0 text-strong" strokeWidth={2.5} />}
            </button>
          )
        })}
      </div>
    </div>
  )
}
